<template>
  <div class="content">
    <base-header class="pb-6">
      <div class="row align-items-center py-4">
        <div class="col-lg-6 col-7">
          <h6 class="h2 text-white d-inline-block mb-0">
            {{ $t("modern_trade.product.title") }}
          </h6>
          <nav aria-label="breadcrumb" class="d-none d-md-inline-block ml-md-4">
            <route-bread-crumb></route-bread-crumb>
          </nav>
        </div>
        <div class="col-lg-6 col-5 text-right">
          <base-button size="md" type="neutral" @click="downloadTemplate">
            <i class="fa fa-download"></i> {{ $t("modern_trade.product.import_template") }}
          </base-button>
          <base-button v-if="isEditModernTrade" size="md" type="neutral" @click="openImport">
            <i class="fa fa-file-import"></i> {{ $t("modern_trade.product.import") }}
          </base-button>
          <base-button v-if="isEditModernTrade" size="md" type="neutral" @click="openCreate">
            <i class="fa fa-plus"></i> {{ $t("modern_trade.product.create") }}
          </base-button>
        </div>
      </div>
    </base-header>

    <div class="container-fluid mt--6">
      <card class="no-border-card" body-classes="px-0 pb-1" footer-classes="pb-2">
        <template slot="header">
          <div class="row">
            <div class="col-md-5">
              <base-input
                v-model="search"
                prepend-icon="fas fa-search"
                :placeholder="$t('modern_trade.product.search_placeholder')"
                @keyup.enter.native="onSearch"
              ></base-input>
            </div>
            <div class="col-md-3">
              <base-input>
                <el-select
                  v-model="statusFilter"
                  :placeholder="$t('modern_trade.product.status')"
                  clearable
                  @change="onSearch"
                  class="w-100"
                >
                  <el-option :label="$t('modern_trade.product.active')" :value="1"></el-option>
                  <el-option :label="$t('modern_trade.product.inactive')" :value="0"></el-option>
                </el-select>
              </base-input>
            </div>
            <div class="col-md-4 text-right">
              <base-button size="sm" type="primary" @click="onSearch">
                <i class="fa fa-search"></i> {{ $t("search") }}
              </base-button>
            </div>
          </div>
        </template>

        <div v-loading="loading">
          <el-table :data="rows" row-key="id" header-row-class-name="thead-light">
            <el-table-column :label="$t('modern_trade.product.code')" min-width="110">
              <template slot-scope="{ row }">{{ row.code }}</template>
            </el-table-column>
            <el-table-column :label="$t('modern_trade.product.name')" min-width="200">
              <template slot-scope="{ row }">{{ row.name }}</template>
            </el-table-column>
            <el-table-column :label="$t('modern_trade.product.category')" min-width="120">
              <template slot-scope="{ row }">{{ row.category || "-" }}</template>
            </el-table-column>
            <el-table-column :label="$t('modern_trade.product.manufacturer')" min-width="150">
              <template slot-scope="{ row }">{{ row.manufacturer || "-" }}</template>
            </el-table-column>
            <el-table-column :label="$t('modern_trade.product.size_value')" min-width="90" align="right">
              <template slot-scope="{ row }">{{ sizeLabel(row) }}</template>
            </el-table-column>
            <el-table-column :label="$t('modern_trade.product.unit_sell')" min-width="100">
              <template slot-scope="{ row }">{{ row.unit_sell || "-" }}</template>
            </el-table-column>
            <el-table-column :label="$t('modern_trade.product.default_retail_price')" min-width="120" align="right">
              <template slot-scope="{ row }">{{ money(row.default_retail_price) }}</template>
            </el-table-column>
            <el-table-column :label="$t('modern_trade.product.sku_code')" min-width="200">
              <template slot-scope="{ row }">
                <template v-if="row.wms_product_id">
                  <span class="badge badge-success" :title="$t('modern_trade.product.sku_mapped')">
                    <i class="fa fa-check"></i> {{ row.wms_man_pn || row.sku_code }}
                  </span>
                  <small v-if="row.wms_name" class="d-block text-muted mt-1">{{ row.wms_name }}</small>
                </template>
                <span
                  v-else
                  class="badge badge-warning"
                  :title="$t('modern_trade.product.sku_unmapped')"
                >
                  <i class="fa fa-times"></i> {{ row.sku_code || $t("modern_trade.product.sku_unmapped") }}
                </span>
              </template>
            </el-table-column>
            <el-table-column :label="$t('modern_trade.product.status')" min-width="100" align="center">
              <template slot-scope="{ row }">
                <span class="badge" :class="row.status === 1 ? 'badge-success' : 'badge-secondary'">
                  {{ row.status === 1 ? $t("modern_trade.product.active") : $t("modern_trade.product.inactive") }}
                </span>
              </template>
            </el-table-column>
            <el-table-column :label="$t('actions')" min-width="120" align="center">
              <template slot-scope="{ row }">
                <base-button size="sm" type="info" @click.native="openEdit(row)">
                  <i class="fa fa-edit"></i>
                </base-button>
                <base-button
                  v-if="isEditModernTrade"
                  size="sm"
                  type="danger"
                  @click.native="handleDelete(row)"
                >
                  <i class="fa fa-trash"></i>
                </base-button>
              </template>
            </el-table-column>
          </el-table>
        </div>

        <div slot="footer" class="d-flex align-items-center justify-content-between">
          <p class="text-muted mb-0">{{ $t("total") }}: {{ total }}</p>
          <base-pagination
            v-model="page"
            :per-page="perPage"
            :total="total"
            @input="loadData"
          ></base-pagination>
        </div>
      </card>
    </div>

    <!-- Create / Edit Dialog -->
    <el-dialog
      :title="isEdit ? $t('modern_trade.product.edit') : $t('modern_trade.product.create')"
      :visible.sync="showDialog"
      width="720px"
      @closed="resetForm"
    >
      <div class="row">
        <div class="col-md-4">
          <div class="form-group">
            <label class="form-control-label">{{ $t("modern_trade.product.code") }} *</label>
            <input v-model="form.code" class="form-control" type="text" :disabled="!isEditModernTrade" />
          </div>
        </div>
        <div class="col-md-8">
          <div class="form-group">
            <label class="form-control-label">{{ $t("modern_trade.product.name") }} *</label>
            <input v-model="form.name" class="form-control" type="text" :disabled="!isEditModernTrade" />
          </div>
        </div>
        <div class="col-md-6">
          <div class="form-group">
            <label class="form-control-label">
              {{ $t("modern_trade.product.category") }}
              <a v-if="isEditModernTrade" href="javascript:;" class="ml-2 small" @click="openCategoryManage">
                <i class="fa fa-cog"></i> {{ $t("modern_trade.product.category_manage") }}
              </a>
            </label>
            <el-select
              v-model="form.category"
              class="w-100"
              filterable
              clearable
              :loading="categoriesLoading"
              :disabled="!isEditModernTrade"
              :placeholder="$t('modern_trade.product.category')"
            >
              <el-option v-for="ct in categories" :key="ct.id" :label="ct.name" :value="ct.name"></el-option>
            </el-select>
          </div>
        </div>
        <div class="col-md-6">
          <div class="form-group">
            <label class="form-control-label">{{ $t("modern_trade.product.manufacturer") }}</label>
            <el-select
              v-model="form.manufacturer"
              class="w-100"
              filterable
              clearable
              :loading="partnersLoading"
              :disabled="!isEditModernTrade"
              :placeholder="$t('modern_trade.product.manufacturer')"
            >
              <el-option v-for="pt in wmsPartners" :key="pt.id" :label="pt.company_name" :value="pt.company_name"></el-option>
            </el-select>
          </div>
        </div>
        <div class="col-md-4">
          <div class="form-group">
            <label class="form-control-label">{{ $t("modern_trade.product.unit_buy") }}</label>
            <input v-model="form.unit_buy" class="form-control" type="text" :disabled="!isEditModernTrade" />
          </div>
        </div>
        <div class="col-md-4">
          <div class="form-group">
            <label class="form-control-label">{{ $t("modern_trade.product.unit_sell") }}</label>
            <input v-model="form.unit_sell" class="form-control" type="text" :disabled="!isEditModernTrade" />
          </div>
        </div>
        <div class="col-md-4">
          <div class="form-group">
            <label class="form-control-label">{{ $t("modern_trade.product.unit_stock") }}</label>
            <input v-model="form.unit_stock" class="form-control" type="text" :disabled="!isEditModernTrade" />
          </div>
        </div>
        <div class="col-md-3">
          <div class="form-group">
            <label class="form-control-label">{{ $t("modern_trade.product.size_value") }}</label>
            <input v-model.number="form.size_value" class="form-control" type="number" step="0.01" min="0" :disabled="!isEditModernTrade" />
          </div>
        </div>
        <div class="col-md-3">
          <div class="form-group">
            <label class="form-control-label">{{ $t("modern_trade.product.size_unit") }}</label>
            <input v-model="form.size_unit" class="form-control" type="text" :disabled="!isEditModernTrade" />
          </div>
        </div>
        <div class="col-md-6">
          <div class="form-group">
            <label class="form-control-label">{{ $t("modern_trade.product.default_retail_price") }}</label>
            <input v-model.number="form.default_retail_price" class="form-control" type="number" step="0.01" min="0" :disabled="!isEditModernTrade" />
          </div>
        </div>
        <div class="col-md-6">
          <div class="form-group">
            <label class="form-control-label">{{ $t("modern_trade.product.sku_code") }}</label>
            <el-select
              v-model="form.sku_code"
              class="w-100"
              filterable
              clearable
              :loading="wmsLoading"
              :disabled="!isEditModernTrade"
              :placeholder="$t('modern_trade.product.sku_code')"
            >
              <el-option
                v-for="wp in wmsProducts"
                :key="wp.id"
                :label="`${wp.sku} — ${wp.name}`"
                :value="wp.sku"
              ></el-option>
            </el-select>
            <small class="text-muted">{{ $t("modern_trade.product.sku_not_resolved") }}</small>
          </div>
        </div>
        <div class="col-md-6">
          <div class="form-group">
            <label class="form-control-label">{{ $t("modern_trade.product.account_code") }}</label>
            <input v-model="form.account_code" class="form-control" type="text" :disabled="!isEditModernTrade" />
          </div>
        </div>
        <div class="col-md-6">
          <div class="form-group">
            <label class="form-control-label">{{ $t("modern_trade.product.status") }}</label>
            <el-select v-model="form.status" class="w-100" :disabled="!isEditModernTrade">
              <el-option :label="$t('modern_trade.product.active')" :value="1"></el-option>
              <el-option :label="$t('modern_trade.product.inactive')" :value="0"></el-option>
            </el-select>
          </div>
        </div>
        <div class="col-12">
          <div class="form-group">
            <label class="form-control-label">{{ $t("modern_trade.product.note") }}</label>
            <textarea v-model="form.note" class="form-control" rows="2" :disabled="!isEditModernTrade"></textarea>
          </div>
        </div>
      </div>
      <span slot="footer">
        <el-button @click="showDialog = false">{{ $t("close") }}</el-button>
        <el-button v-if="isEditModernTrade" type="primary" :loading="saving" @click="handleSave">{{ $t("save") }}</el-button>
      </span>
    </el-dialog>

    <!-- Import Wizard Dialog -->
    <el-dialog
      :title="$t('modern_trade.product.import')"
      :visible.sync="showImport"
      width="820px"
      @closed="resetImport"
    >
      <!-- Step 1: pick file -->
      <div v-if="importStep === 1">
        <div class="row align-items-end">
          <div class="col-md-8">
            <label class="form-control-label">{{ $t("modern_trade.import.pick_file") }} (.xlsx)</label>
            <input
              ref="importFileInput"
              type="file"
              accept=".xlsx,.xls"
              class="form-control"
              @change="onImportFileChange"
            />
          </div>
          <div class="col-md-4 pb-1">
            <base-button type="default" @click="downloadTemplate">
              <i class="fa fa-download"></i> {{ $t("modern_trade.product.import_template") }}
            </base-button>
          </div>
        </div>
      </div>

      <!-- Step 2: preview -->
      <div v-if="importStep === 2">
        <div class="mb-3">
          <span class="badge badge-secondary mr-2">{{ $t("modern_trade.import.summary_total") }}: {{ importBatch.row_total }}</span>
          <span class="badge badge-success mr-2">{{ $t("modern_trade.import.summary_ok") }}: {{ importBatch.row_ok }}</span>
          <span class="badge badge-danger mr-2">{{ $t("modern_trade.import.summary_error") }}: {{ importBatch.row_error }}</span>
        </div>
        <el-table :data="importRows" header-row-class-name="thead-light" size="small" max-height="380">
          <el-table-column :label="$t('modern_trade.import.row_no')" prop="row_no" width="60" align="center"></el-table-column>
          <el-table-column :label="$t('modern_trade.product.code')" prop="code" min-width="110"></el-table-column>
          <el-table-column :label="$t('modern_trade.product.name')" min-width="180">
            <template slot-scope="{ row }">{{ row.name || "-" }}</template>
          </el-table-column>
          <el-table-column :label="$t('status')" min-width="90" align="center">
            <template slot-scope="{ row }">
              <span class="badge" :class="row.status === 1 ? 'badge-success' : 'badge-danger'">
                {{ row.status === 1 ? $t("modern_trade.import.status_ok") : $t("modern_trade.import.status_error") }}
              </span>
            </template>
          </el-table-column>
          <el-table-column :label="$t('modern_trade.import.error_message')" min-width="220">
            <template slot-scope="{ row }">
              <span v-if="row.error_msg" class="text-danger small">{{ row.error_msg }}</span>
            </template>
          </el-table-column>
        </el-table>
      </div>

      <span slot="footer">
        <template v-if="importStep === 1">
          <el-button @click="showImport = false">{{ $t("close") }}</el-button>
          <el-button type="primary" :loading="validating" :disabled="!importFile" @click="doValidate">
            {{ $t("modern_trade.import.validate") }}
          </el-button>
        </template>
        <template v-else>
          <el-button @click="resetImport">{{ $t("modern_trade.import.cancel") }}</el-button>
          <el-button
            type="success"
            :loading="confirming"
            :disabled="!importBatch.row_ok"
            @click="doConfirm"
          >
            {{ $t("modern_trade.import.confirm") }}
          </el-button>
        </template>
      </span>
    </el-dialog>

    <!-- จัดการกลุ่มสินค้า -->
    <el-dialog
      :title="$t('modern_trade.product.category_manage')"
      :visible.sync="showCategoryDialog"
      width="480px"
      append-to-body
    >
      <div class="d-flex mb-3">
        <input
          v-model="newCategoryName"
          class="form-control mr-2"
          type="text"
          :placeholder="$t('modern_trade.product.category_add_placeholder')"
          @keyup.enter="addCategory"
        />
        <el-button type="primary" size="small" :loading="categorySaving" @click="addCategory">
          {{ $t("modern_trade.product.category_add") }}
        </el-button>
      </div>
      <el-table :data="categories" size="mini" v-loading="categoriesLoading" max-height="320">
        <el-table-column :label="$t('modern_trade.product.category_name')" prop="name" min-width="200"></el-table-column>
        <el-table-column width="110" align="right">
          <template slot-scope="{ row }">
            <el-button size="mini" type="info" icon="el-icon-edit" @click="renameCategory(row)"></el-button>
            <el-button size="mini" type="danger" icon="el-icon-delete" @click="removeCategory(row)"></el-button>
          </template>
        </el-table-column>
      </el-table>
    </el-dialog>
  </div>
</template>

<script>
import { Table, TableColumn, Select, Option, Dialog, Button } from "element-ui";
import RouteBreadCrumb from "@/components/Breadcrumb/RouteBreadcrumb";
import { BasePagination } from "@/components";
import swal from "sweetalert2";
import { mapGetters } from "vuex";
import { ModernTradeService } from "@/resource";

const emptyForm = () => ({
  code: "",
  name: "",
  category: "",
  manufacturer: "",
  unit_buy: "",
  unit_sell: "",
  unit_stock: "",
  size_value: null,
  size_unit: "",
  sku_code: "",
  default_retail_price: null,
  account_code: "",
  note: "",
  status: 1,
});

export default {
  name: "MtProducts",
  components: {
    RouteBreadCrumb,
    BasePagination,
    [Table.name]: Table,
    [TableColumn.name]: TableColumn,
    [Select.name]: Select,
    [Option.name]: Option,
    [Dialog.name]: Dialog,
    [Button.name]: Button,
  },
  computed: {
    ...mapGetters({
      isAccessModernTrade: "Auth/isAccessModernTrade",
      isEditModernTrade: "Auth/isEditModernTrade",
    }),
  },
  data() {
    return {
      loading: false,
      saving: false,
      rows: [],
      total: 0,
      page: 1,
      perPage: 20,
      search: "",
      statusFilter: null,
      showDialog: false,
      isEdit: false,
      editId: null,
      wmsProducts: [],
      wmsLoading: false,
      wmsPartners: [],
      partnersLoading: false,
      categories: [],
      categoriesLoading: false,
      showCategoryDialog: false,
      newCategoryName: "",
      categorySaving: false,
      form: emptyForm(),
      // Import wizard
      showImport: false,
      importStep: 1,
      importFile: null,
      validating: false,
      confirming: false,
      importBatch: { id: null, row_total: 0, row_ok: 0, row_error: 0 },
      importRows: [],
    };
  },
  mounted() {
    this.loadData();
  },
  methods: {
    async loadData() {
      this.loading = true;
      try {
        const params = { page: this.page, per_page: this.perPage };
        if (this.search) params.search = this.search;
        if (this.statusFilter !== null && this.statusFilter !== "") params.status = this.statusFilter;
        const res = await ModernTradeService.productsList(params);
        const d = (res.data && res.data.Data && res.data.Data[0]) || {};
        this.rows = d.items || [];
        this.total = d.total || 0;
      } catch (err) {
        swal.fire({ title: (err.response && err.response.data && err.response.data.Cause) || err.message, icon: "error" });
      } finally {
        this.loading = false;
      }
    },
    onSearch() {
      this.page = 1;
      this.loadData();
    },
    sizeLabel(row) {
      if (row.size_value === null || row.size_value === undefined || row.size_value === "") return "-";
      const v = Number(row.size_value);
      const num = Number.isInteger(v) ? String(v) : String(v);
      return row.size_unit ? `${num} ${row.size_unit}` : num;
    },
    money(v) {
      if (v === null || v === undefined || v === "") return "-";
      const n = Number(v);
      if (isNaN(n)) return "-";
      return n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    async loadWmsProducts() {
      if (this.wmsProducts.length || this.wmsLoading) return;
      this.wmsLoading = true;
      try {
        const res = await ModernTradeService.wmsProductOptions();
        const d = (res.data && res.data.Data && res.data.Data[0]) || {};
        const items = d.items || [];
        // SKU ที่แสดง/บันทึก = man_pn (BE คัดเฉพาะแถวที่มี man_pn มาแล้ว)
        this.wmsProducts = items
          .map((p) => ({ id: p.id, name: p.name || "", sku: p.man_pn || "" }))
          .filter((p) => p.sku)
          .sort((a, b) => a.sku.localeCompare(b.sku));
      } catch (err) {
        // โหลดรายการไม่ได้ก็ยังพิมพ์เอง/เคลียร์ค่าใน select ได้ — แจ้งเงียบ ๆ พอ
        console.warn("load wms products failed", err);
      } finally {
        this.wmsLoading = false;
      }
    },
    async loadWmsPartners() {
      if (this.wmsPartners.length || this.partnersLoading) return;
      this.partnersLoading = true;
      try {
        const res = await ModernTradeService.wmsPartnerOptions();
        const d = (res.data && res.data.Data && res.data.Data[0]) || {};
        this.wmsPartners = d.items || [];
      } catch (err) {
        console.warn("load wms partners failed", err);
      } finally {
        this.partnersLoading = false;
      }
    },
    async loadCategories(force) {
      if (!force && (this.categories.length || this.categoriesLoading)) return;
      this.categoriesLoading = true;
      try {
        const res = await ModernTradeService.categoriesList();
        const d = (res.data && res.data.Data && res.data.Data[0]) || {};
        this.categories = d.items || [];
      } catch (err) {
        console.warn("load categories failed", err);
      } finally {
        this.categoriesLoading = false;
      }
    },
    openCategoryManage() {
      this.newCategoryName = "";
      this.showCategoryDialog = true;
      this.loadCategories();
    },
    async addCategory() {
      const name = (this.newCategoryName || "").trim();
      if (!name) return;
      this.categorySaving = true;
      try {
        await ModernTradeService.categoryCreate({ name });
        this.newCategoryName = "";
        await this.loadCategories(true);
      } catch (err) {
        swal.fire({ title: (err.response && err.response.data && err.response.data.Cause) || err.message, icon: "error" });
      } finally {
        this.categorySaving = false;
      }
    },
    async renameCategory(row) {
      const result = await swal.fire({
        title: this.$t("modern_trade.product.category_name"),
        input: "text",
        inputValue: row.name,
        showCancelButton: true,
        confirmButtonText: this.$t("save"),
        cancelButtonText: this.$t("cancel"),
      });
      const name = (result.value || "").trim();
      if (!name || name === row.name) return;
      try {
        await ModernTradeService.categoryUpdate(row.id, { name });
        await this.loadCategories(true);
        this.load();
      } catch (err) {
        swal.fire({ title: (err.response && err.response.data && err.response.data.Cause) || err.message, icon: "error" });
      }
    },
    async removeCategory(row) {
      const result = await swal.fire({
        title: this.$t("modern_trade.product.category_delete_confirm"),
        text: row.name,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: this.$t("confirm"),
        cancelButtonText: this.$t("cancel"),
      });
      if (!result.value) return;
      try {
        await ModernTradeService.categoryDelete(row.id);
        await this.loadCategories(true);
      } catch (err) {
        swal.fire({ title: (err.response && err.response.data && err.response.data.Cause) || err.message, icon: "error" });
      }
    },
    openCreate() {
      this.isEdit = false;
      this.editId = null;
      this.form = emptyForm();
      this.showDialog = true;
      this.loadWmsProducts();
      this.loadWmsPartners();
      this.loadCategories();
    },
    openEdit(row) {
      this.isEdit = true;
      this.editId = row.id;
      this.form = {
        code: row.code || "",
        name: row.name || "",
        category: row.category || "",
        manufacturer: row.manufacturer || "",
        unit_buy: row.unit_buy || "",
        unit_sell: row.unit_sell || "",
        unit_stock: row.unit_stock || "",
        size_value: row.size_value !== undefined && row.size_value !== null ? Number(row.size_value) : null,
        size_unit: row.size_unit || "",
        sku_code: row.sku_code || "",
        default_retail_price: row.default_retail_price !== undefined && row.default_retail_price !== null ? Number(row.default_retail_price) : null,
        account_code: row.account_code || "",
        note: row.note || "",
        status: row.status !== undefined ? row.status : 1,
      };
      this.showDialog = true;
      this.loadWmsProducts();
      this.loadWmsPartners();
      this.loadCategories();
    },
    resetForm() {
      this.form = emptyForm();
    },
    async handleSave() {
      if (!this.form.code) {
        swal.fire({ title: this.$t("modern_trade.product.code_required"), icon: "warning" });
        return;
      }
      if (!this.form.name) {
        swal.fire({ title: this.$t("modern_trade.product.name_required"), icon: "warning" });
        return;
      }
      this.saving = true;
      try {
        if (this.isEdit) {
          await ModernTradeService.productUpdate(this.editId, this.form);
        } else {
          await ModernTradeService.productCreate(this.form);
        }
        swal.fire({ title: this.$t("modern_trade.product.saved"), icon: "success", timer: 1500, showConfirmButton: false });
        this.showDialog = false;
        this.loadData();
      } catch (err) {
        const status = err.response && err.response.status;
        const cause = (err.response && err.response.data && err.response.data.Cause) || err.message;
        let title = cause;
        if (status === 400 && cause && /sku|barcode|man_pn|resolve/i.test(cause)) {
          title = this.$t("modern_trade.product.sku_not_resolved");
        } else if (status === 409 || (cause && /duplicate|unique|exists/i.test(cause))) {
          title = this.$t("modern_trade.product.code_duplicate");
        }
        swal.fire({ title, icon: "error" });
      } finally {
        this.saving = false;
      }
    },
    async handleDelete(row) {
      const result = await swal.fire({
        title: this.$t("modern_trade.product.delete_confirm"),
        text: `${row.code} - ${row.name}`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: this.$t("confirm") || "Confirm",
        cancelButtonText: this.$t("cancel") || "Cancel",
      });
      if (!result.value) return;
      try {
        await ModernTradeService.productDelete(row.id);
        this.loadData();
      } catch (err) {
        const status = err.response && err.response.status;
        const cause = (err.response && err.response.data && err.response.data.Cause) || err.message;
        // 409 in-use → suggest switching to inactive instead of deleting.
        const title = status === 409 ? this.$t("modern_trade.product.has_reference_cannot_delete") : cause;
        swal.fire({ title, icon: "error" });
      }
    },
    async downloadTemplate() {
      try {
        const res = await ModernTradeService.productImportTemplate();
        this.triggerBlobDownload(res.data, "mt_product_template.xlsx");
      } catch (err) {
        swal.fire({ title: (err.response && err.response.data && err.response.data.Cause) || err.message, icon: "error" });
      }
    },
    triggerBlobDownload(data, filename) {
      const blob = new Blob([data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    },
    openImport() {
      this.resetImport();
      this.showImport = true;
    },
    onImportFileChange(e) {
      this.importFile = e.target.files[0] || null;
    },
    async doValidate() {
      if (!this.importFile) return;
      this.validating = true;
      try {
        const fd = new FormData();
        fd.append("file", this.importFile);
        const res = await ModernTradeService.productImportValidate(fd);
        const d = (res.data && res.data.Datum) || (res.data && res.data.Data && res.data.Data[0]) || {};
        this.importBatch = {
          id: d.batch_id,
          row_total: d.row_total || 0,
          row_ok: d.row_ok || 0,
          row_error: d.row_error || 0,
        };
        this.importRows = d.rows || [];
        this.importStep = 2;
      } catch (err) {
        swal.fire({ title: (err.response && err.response.data && err.response.data.Cause) || err.message, icon: "error" });
      } finally {
        this.validating = false;
      }
    },
    async doConfirm() {
      if (!this.importBatch.row_ok) return;
      this.confirming = true;
      try {
        await ModernTradeService.productImportConfirm({ batch_id: this.importBatch.id });
        this.importBatch.id = null;
        swal.fire({ title: this.$t("modern_trade.product.import_success"), icon: "success", timer: 1500, showConfirmButton: false });
        this.showImport = false;
        this.loadData();
      } catch (err) {
        swal.fire({ title: (err.response && err.response.data && err.response.data.Cause) || err.message, icon: "error" });
      } finally {
        this.confirming = false;
      }
    },
    resetImport() {
      this.importStep = 1;
      this.importFile = null;
      this.importBatch = { id: null, row_total: 0, row_ok: 0, row_error: 0 };
      this.importRows = [];
      if (this.$refs.importFileInput) this.$refs.importFileInput.value = "";
    },
  },
};
</script>
